import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Col, FormGroup, Label } from "reactstrap";
import Flatpickr from "react-flatpickr";
import CashBookEntry from "./index";
import CashierVerifyModal from "./Cashierverifymodal";
import { commonPageFieldList, commonPageFieldListSuccess } from "../../../store/actions";
import CommonPurchaseList from "../../../components/Common/CommonPurchaseList";
import * as pageId from "../../../routes/allPageID";
import * as url from "../../../routes/route_url";
import * as mode from "../../../routes/PageMode";
import { Go_Button, PageLoadingSpinner } from "../../../components/Common/CommonButton";
import { customAlert } from "../../../CustomAlert/ConfirmDialog";
import { C_Select } from "../../../CustomValidateForm";
import { breadcrumbReturnFunc, date_ymd_func, loginSelectedPartyID } from "../../../components/Common/CommonFunction";
import { CashierName_Api } from "../../../helpers/backend_helper";
import { useHistory } from "react-router-dom";
import { deleteCashBookEntryID, deleteCashBookEntryIDSuccess, editCashBookEntryID, editCashBookEntryIDSuccess, getCashBookEntryList, getCashBookEntryListSuccess, updateCashBookEntryIDSuccess } from "../../../store/Administrator/CashBookEntryRedux/action";
import { allLabelWithZero } from "../../../components/Common/CommonErrorMsg/HarderCodeData";

// always returns Y-m-d using local date parts (no toISOString, so no timezone shift)
const toYMD = (value) => {
    if (!value) return "";
    if (value instanceof Date) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, "0");
        const d = String(value.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const dmy = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
    return s;
};
const CashBookEntryList = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = { ...history.location };
    const reducers = useSelector((state) => ({
        listBtnLoading: state.CashBookEntryReducer.listBtnLoading,
        goBtnLoading: state.CashBookEntryReducer.goBtnLoading,
        tableList: state.CashBookEntryReducer.CashBookEntryListData,
        editData: state.CashBookEntryReducer.editData,
        updateMsg: state.CashBookEntryReducer.updateMessage,
        deleteMsg: state.CashBookEntryReducer.deleteMessage,
        userAccess: state.Login.RoleAccessUpdateData,
        postMsg: state.CashBookEntryReducer.postMsg,
        pageField: state.CommonPageFieldReducer.pageFieldList,
    }));
    const { pageField, goBtnLoading, userAccess, tableList } = reducers;
    const lastFilterRef = useRef(null);
    // ---- filters ----
    const [fromDate, setFromDate] = useState(() => date_ymd_func());   // Y-m-d
    const [cashier, setCashier] = useState(allLabelWithZero);
    const [cashierOptions, setCashierOptions] = useState([]);
    const [editCashier, setEditCashier] = useState(null);
    const goClickedRef = useRef(false);
    // ---- verify before edit ----
    const [verifyOpen, setVerifyOpen] = useState(false);
    const pendingEditRef = useRef(null);   // edit request waiting for the password check
    const editInterceptor = (config) => {
        const row = config.rowData;
        // CashierName is already on the row, so the modal can use it directly.
        // Prefer the option from the dropdown list if it exists (it may carry UserName).
        const rowCashier =
            cashierOptions.find((o) => String(o.value) === String(row?.CashierID)) ||
            (row?.CashierID ? { value: row.CashierID, label: row.CashierName } : null);

        if (!rowCashier?.label) {
            customAlert({ Type: 3, Message: "Cashier not found for this record" });
            return { type: "CASH_BOOK_ENTRY_EDIT_WAITING_FOR_VERIFY" };
        }

        pendingEditRef.current = config;
        setEditCashier(rowCashier);
        setVerifyOpen(true);
        return { type: "CASH_BOOK_ENTRY_EDIT_WAITING_FOR_VERIFY" };
    };

    const deleteInterceptor = (config) => {
        const row = config.rowData;

        dispatch(
            deleteCashBookEntryID({
                ...config,
                jsonBody: JSON.stringify({
                    PartyID: row?.PartyID ?? loginSelectedPartyID(),
                    Date: toYMD(row?.Date ?? fromDate),
                    CashierID: row?.CashierID ?? 0,
                }),
            })
        );

        return {
            type: "CASH_BOOK_ENTRY_DELETE_REQUESTED",
        };
    };

    const dateOptions = useMemo(() => ({
        altInput: true,
        altFormat: "d-m-Y",
        dateFormat: "Y-m-d",
    }), []);

    // Y-m-d string -> local Date (no timezone shift)
    const fromDateObj = useMemo(() => {
        const [y, m, d] = (fromDate || "").split("-").map(Number);
        return y ? new Date(y, m - 1, d) : null;
    }, [fromDate]);


    const mappedTableList = useMemo(() => (tableList || []).map((row) => ({
        ...row,
        CashierName:
            cashierOptions.find((o) => String(o.value) === String(row.CashierID))?.label || "",
    })), [tableList, cashierOptions]);

    const verifiedHandler = () => {
        setVerifyOpen(false);
        const config = pendingEditRef.current;
        pendingEditRef.current = null;

        if (config) {
            dispatch(editCashBookEntryID({
                ...config,   // keeps btnId, btnmode, editId
                jsonBody: JSON.stringify({
                    ...buildFilter(),
                    CashierID: config.rowData?.CashierID ?? 0,
                    Mode: 2,
                }),
            }));
        }
    };

    const closeVerifyHandler = () => {
        setVerifyOpen(false);
        pendingEditRef.current = null;
    };

    // same keys as CashBookHeadList: editId is the only one replaced (by the verify interceptor)
    const action = {
        getList: getCashBookEntryList,
        editId: editInterceptor,
        updateSucc: updateCashBookEntryIDSuccess,
        deleteId: deleteInterceptor,
        deleteSucc: deleteCashBookEntryIDSuccess,
    };

    const buildFilter = () => {
        const filter = {
            PartyID: loginSelectedPartyID(),
            Date: toYMD(fromDate),
            Mode: 1,
        };

        if (cashier?.value && Number(cashier.value) !== 0) {
            filter.CashierID = Number(cashier.value);
        }

        return filter;
    };

    const fetchList = (filter) => {
        lastFilterRef.current = filter;
        dispatch(getCashBookEntryList({ jsonBody: JSON.stringify(filter) }));
    };

    const goHandler = () => {
        if (!fromDate) {
            customAlert({ Type: 3, Message: "Please select Date" });
            return;
        }
        goClickedRef.current = true;
        fetchList(buildFilter());
    };

    const refreshList = () => fetchList(buildFilter());
    // page fields + cashier dropdown
    useEffect(() => {
        dispatch(commonPageFieldListSuccess(null));
        dispatch(commonPageFieldList(pageId.CASH_BOOK_ENTRY_LIST));

        // Load records automatically when entering the page
        const initialFilter = {
            PartyID: loginSelectedPartyID(),
            CashierID: 0,
            Date: date_ymd_func(),
            Mode: 1,
        };
        lastFilterRef.current = initialFilter;
        dispatch(
            getCashBookEntryList({
                jsonBody: JSON.stringify(initialFilter),
            })
        );
        // Load cashier dropdown options
        (async () => {
            try {
                const resp = await CashierName_Api({
                    jsonBody: JSON.stringify({
                        Party: loginSelectedPartyID(),
                    }),
                });

                if (resp?.StatusCode === 200) {
                    setCashierOptions([
                        allLabelWithZero,
                        ...(resp.Data || []),
                    ]);
                } else if (resp?.Message) {
                    customAlert({
                        Type: 4,
                        Message: resp.Message,
                    });
                }
            } catch (e) {
                setCashierOptions([allLabelWithZero]);
                customAlert({
                    Type: 4,
                    Message: "Unable to load Cashiers",
                });
            }
        })();

        return () => {
            dispatch(getCashBookEntryListSuccess([]));
            dispatch(editCashBookEntryIDSuccess({ Status: false }));
            dispatch(commonPageFieldListSuccess(null));
        };
    }, []);

    useEffect(() => {
        if (goBtnLoading || !goClickedRef.current) return;   // still loading, or not from a Go click

        goClickedRef.current = false;
        if (!tableList?.length) {
            customAlert({ Type: 3, Message: "No records available" });
        }
    }, [goBtnLoading]);

    useEffect(() => {
        const userAcc = userAccess.find(
            (inx) => `/${inx.ActualPagePath}` === location.pathname
        );

        if (userAcc) {
            breadcrumbReturnFunc({
                dispatch,
                userAcc,
                newBtnPath: url.CASH_BOOK_ENTRY,
                pageField,
            });
        }
    }, [userAccess, dispatch, location.pathname, pageField]);

    return (
        <React.Fragment>
            <PageLoadingSpinner isLoading={goBtnLoading || !pageField} />
            <div className="page-content">
                {/* ---------------- filter bar ---------------- */}
                <div className="px-2 c_card_filter text-black">
                    <div className="row">
                        <Col sm="4">
                            <FormGroup className="mb-3 row mt-3 align-items-center">
                                <Label className="mt-2" style={{ width: "70px" }}>Date</Label>
                                <Col sm={8}>
                                    <Flatpickr
                                        id="entryListDate"
                                        name="Date"
                                        className="form-control d-block p-2 bg-white text-dark"
                                        value={fromDateObj ? [fromDateObj] : []}
                                        options={dateOptions}
                                        onChange={(selectedDates) => {
                                            if (selectedDates?.[0]) setFromDate(toYMD(selectedDates[0]));
                                        }}
                                    />
                                </Col>
                            </FormGroup>
                        </Col>

                        <Col sm="4">
                            <FormGroup className="mb-3 row mt-3 align-items-center">
                                <Label className="mt-2" style={{ width: "70px" }}>Cashier</Label>
                                <Col sm={8}>
                                    <C_Select
                                        name="Cashier"
                                        value={cashier}
                                        isSearchable={true}
                                        isClearable={true}
                                        className="react-dropdown"
                                        classNamePrefix="dropdown"
                                        styles={{ menu: (provided) => ({ ...provided, zIndex: 2 }) }}
                                        options={cashierOptions}
                                        onChange={(selected) => setCashier(selected || allLabelWithZero)}
                                    />
                                </Col>
                            </FormGroup>
                        </Col>

                        <Col sm="4" className="mt-3 text-end pe-5">
                            <Go_Button onClick={goHandler} loading={goBtnLoading} />
                        </Col>
                    </div>
                </div>

                {/* ---------------- table (columns come from the list PageField) ---------------- */}
                {pageField && (
                    <CommonPurchaseList
                        action={action}
                        reducers={{ ...reducers, tableList: mappedTableList }}
                        showBreadcrumb={false}
                        masterPath={url.CASH_BOOK_ENTRY}
                        newBtnPath={url.CASH_BOOK_ENTRY}
                        pageMode={mode.defaultList}
                        goButnFunc={refreshList}
                        MasterModal={CashBookEntry}
                        ButtonMsgLable={"CashBook Entry"}
                        deleteName={"CashierName"}
                    />
                )}
            </div>

            {/* ---------------- verify popup shown before edit ---------------- */}
            <CashierVerifyModal
                isOpen={verifyOpen}
                toggle={closeVerifyHandler}
                cashier={editCashier}
                onVerified={verifiedHandler}
            />
        </React.Fragment>
    );
};

export default CashBookEntryList;